import { AbilityBuilder, createMongoAbility } from '@casl/ability'
import { createContextualCan } from '@casl/react'
import type { ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'

import type { AppAbility } from '@/libs/ability'
import { defineAbilitiesFor, defineAbilitiesFromPermissions } from '@/libs/ability'
import { useAppSelector } from '@/store/hooks'

const AbilityContext = createContext<AppAbility>({} as AppAbility)

export const Can = createContextualCan(AbilityContext.Consumer)

// Superadmins aren't governed by org-scoped role permissions — the backend
// has no permission model for them yet, so the role-to-permissions fetch
// never has anything to return. Without this they'd be stuck behind the
// deny-all fallback and see an empty app.
const defineSuperAdminAbility = (): AppAbility => {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility)
  can('manage', 'all')
  return build()
}

export function AbilityProvider({ children }: { children: ReactNode }) {
  const permissions = useAppSelector((state) => state.auth.permissions)
  const isSuperAdmin = useAppSelector((state) => state.auth.user?.isSuperAdmin ?? false)

  const ability = useMemo((): AppAbility => {
    if (permissions && permissions.length > 0) {
      return defineAbilitiesFromPermissions(permissions)
    }
    if (isSuperAdmin) {
      return defineSuperAdminAbility()
    }
    // Fallback: no permissions yet (pre-login) — deny everything
    return defineAbilitiesFor('viewer')
  }, [permissions, isSuperAdmin])

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
}

export function useAbility() {
  const context = useContext(AbilityContext)
  if (!context) {
    throw new Error('useAbility must be used within an AbilityProvider')
  }
  return context
}
